import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Clock, Plus, Edit, Trash2, RefreshCw, Users,
  ChevronLeft, ChevronRight, UserCheck, Zap, Calendar, History,
  Mail, Send, Settings, Phone, FileText, AlertTriangle, Copy, XCircle
} from 'lucide-react';

const DAYS = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata'];
const AUTO_ASSIGN_DAYS = 5; // Generare automată doar Luni-Vineri (primele 5 zile)

// Generate 24h time options with 30-minute intervals
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const formatDate = (d) => d.toISOString().split('T')[0];
const formatDateRo = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
};
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${d}.${m}`;
};

const ShiftsPage = () => {
  const [shifts, setShifts] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [onDuty, setOnDuty] = useState(null);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [loading, setLoading] = useState(true);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({ name: '', start_time: '08:00', end_time: '17:00', color: '#3B82F6' });
  const [assignForm, setAssignForm] = useState({ technician_id: '', shift_id: '', date: '', assign_mode: 'day' });
  // assign_mode: 'day' = single day, 'week' = whole week (Mon-Sat)
  const [rotating, setRotating] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMonths, setHistoryMonths] = useState('3');
  const [expandedWeek, setExpandedWeek] = useState(null);

  // Report Settings
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportSettings, setReportSettings] = useState({
    enabled: false, frequency: 'weekly', report_week: 'next_week', send_email: true, send_sms: false,
    send_day: 'friday', send_hour: '08:00', recipients: []
  });
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', phone: '' });
  const [sendingReport, setSendingReport] = useState(false);
  const [reportLogs, setReportLogs] = useState([]);
  const [reportLogsLoading, setReportLogsLoading] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const ws = formatDate(weekStart);
      const [shiftsRes, techsRes, assignRes, dutyRes] = await Promise.all([
        api.get('/shifts/definitions'),
        api.get('/shifts/technicians'),
        api.get('/shifts/assignments', { params: { week_start: ws } }),
        api.get('/shifts/on-duty')
      ]);
      setShifts(shiftsRes.data);
      setTechnicians(techsRes.data);
      setAssignments(assignRes.data);
      setOnDuty(dutyRes.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveShift = async () => {
    try {
      if (editShift) {
        await api.put(`/shifts/definitions/${editShift.id}`, shiftForm);
        toast.success('Schimb actualizat');
      } else {
        await api.post('/shifts/definitions', shiftForm);
        toast.success('Schimb creat');
      }
      setShowShiftDialog(false);
      setEditShift(null);
      fetchData();
    } catch { toast.error('Eroare la salvare'); }
  };

  const handleDeleteShift = async (id) => {
    if (!window.confirm('Stergeti acest schimb?')) return;
    try {
      await api.delete(`/shifts/definitions/${id}`);
      toast.success('Schimb sters');
      fetchData();
    } catch { toast.error('Eroare la stergere'); }
  };

  const handleAssign = async () => {
    try {
      if (assignForm.assign_mode === 'week') {
        // Build bulk assignments for Mon-Fri only (first 5 days) - Saturday is manual
        const autoAssignDates = weekDates.slice(0, AUTO_ASSIGN_DAYS);
        const bulkAssignments = autoAssignDates.map(date => ({
          technician_id: assignForm.technician_id,
          shift_id: assignForm.shift_id,
          date: date
        }));
        await api.post('/shifts/assignments/bulk', bulkAssignments);
        toast.success(`Tehnician asignat Luni-Vineri (${AUTO_ASSIGN_DAYS} zile). Sambata se adauga manual.`);
      } else {
        await api.post('/shifts/assignments', {
          technician_id: assignForm.technician_id,
          shift_id: assignForm.shift_id,
          date: assignForm.date
        });
        toast.success('Tehnician asignat');
      }
      setShowAssignDialog(false);
      fetchData();
    } catch { toast.error('Eroare la asignare'); }
  };

  const handleDeleteAssignment = async (id) => {
    try {
      await api.delete(`/shifts/assignments/${id}`);
      toast.success('Asignare stearsa');
      fetchData();
    } catch { toast.error('Eroare'); }
  };

  const handleAutoRotate = async () => {
    setRotating(true);
    try {
      const res = await api.post('/shifts/generate-next-week', {
        source_week_start: formatDate(weekStart)
      });
      toast.success(res.data.message);
      // Navigate to the target week
      const targetDate = new Date(res.data.target_week_start);
      setWeekStart(targetDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la generare');
    } finally { setRotating(false); }
  };

  const openAssignDialog = (date, shiftId, mode = 'day') => {
    setAssignForm({ technician_id: '', shift_id: shiftId || '', date: date || '', assign_mode: mode });
    setShowAssignDialog(true);
  };

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => setWeekStart(getMonday(new Date()));

  const handleClearWeek = async () => {
    if (!window.confirm(`Stergeti TOATE asignarile din saptamana ${weekLabel}?`)) return;
    try {
      const ws = formatDate(weekStart);
      const res = await api.delete(`/shifts/assignments/clear-week/${ws}`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Eroare la golirea saptamanii'); }
  };

  // History
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await api.get('/shifts/history', { params: { months: parseInt(historyMonths) } });
      setHistory(res.data);
    } catch { toast.error('Eroare la incarcarea istoricului'); }
    finally { setHistoryLoading(false); }
  }, [historyMonths]);

  useEffect(() => { if (activeTab === 'history') fetchHistory(); }, [activeTab, fetchHistory]);

  // Report Settings
  const fetchReportSettings = useCallback(async () => {
    try {
      const [res, mailRes] = await Promise.all([
        api.get('/shifts/report-settings'),
        api.get('/mail-settings').catch(() => ({ data: {} }))
      ]);
      setReportSettings(res.data);
      const host = mailRes.data?.smtp_host || '';
      setSmtpConfigured(!!host && host !== 'smtp.example.com');
    } catch {}
  }, []);

  const fetchReportLogs = useCallback(async () => {
    try {
      setReportLogsLoading(true);
      const res = await api.get('/shifts/report-logs?limit=20');
      setReportLogs(res.data);
    } catch {}
    finally { setReportLogsLoading(false); }
  }, []);

  useEffect(() => { fetchReportSettings(); }, [fetchReportSettings]);
  useEffect(() => { if (showReportDialog) fetchReportLogs(); }, [showReportDialog, fetchReportLogs]);

  const handleSaveReportSettings = async () => {
    try {
      // Auto-add pending recipient if fields are filled
      let settingsToSave = { ...reportSettings };
      if (newRecipient.name && (newRecipient.email || newRecipient.phone)) {
        settingsToSave = {
          ...settingsToSave,
          recipients: [...settingsToSave.recipients, { ...newRecipient }]
        };
        setReportSettings(settingsToSave);
        setNewRecipient({ name: '', email: '', phone: '' });
      }
      await api.post('/shifts/report-settings', settingsToSave);
      toast.success('Setari raport salvate');
      setShowReportDialog(false);
    } catch { toast.error('Eroare la salvare'); }
  };

  const addRecipient = () => {
    if (!newRecipient.name || (!newRecipient.email && !newRecipient.phone)) {
      toast.error('Completati numele si cel putin email sau telefon');
      return;
    }
    setReportSettings(prev => ({
      ...prev,
      recipients: [...prev.recipients, { ...newRecipient }]
    }));
    setNewRecipient({ name: '', email: '', phone: '' });
  };

  const removeRecipient = (index) => {
    setReportSettings(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const ws = formatDate(weekStart);
      const res = await api.post('/shifts/send-report', { week_start: ws });
      if (res.data.success) {
        // Check if email/sms actually succeeded
        const emailResults = res.data.results?.email || [];
        const smsResults = res.data.results?.sms || [];
        const emailFailed = emailResults.some(r => !r.success);
        const smsFailed = smsResults.some(r => !r.success);
        
        if (emailFailed || smsFailed) {
          const errors = [];
          emailResults.filter(r => !r.success).forEach(r => errors.push(r.error));
          smsResults.filter(r => !r.success).forEach(r => errors.push(r.error));
          toast.error(`Eroare la trimitere: ${errors.join('; ')}`, { duration: 8000 });
        } else {
          toast.success(res.data.message);
        }
        fetchReportLogs();
      } else {
        toast.error(res.data.message || 'Eroare la trimitere');
      }
    } catch { toast.error('Eroare la trimiterea raportului'); }
    finally { setSendingReport(false); }
  };

  const handlePreviewReport = async () => {
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    try {
      const ws = formatDate(weekStart);
      const res = await api.get('/shifts/report-preview', { params: { week_start: ws } });
      setPreviewHtml(res.data.html || '<p>Niciun continut</p>');
    } catch { toast.error('Eroare la generare preview'); setPreviewHtml('<p>Eroare</p>'); }
    finally { setPreviewLoading(false); }
  };

  // Build week dates
  const weekDates = DAYS.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return formatDate(d);
  });

  const weekLabel = `${formatDateRo(weekDates[0])} — ${formatDateRo(weekDates[5])}`;

  // Group assignments by date and shift
  const getAssignmentsForCell = (date, shiftId) =>
    assignments.filter(a => a.date === date && a.shift_id === shiftId);

  const isToday = (date) => date === formatDate(new Date());

  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Schimburi Tehnicieni</h1>
          <p className="text-slate-500 text-sm mt-1">Planificare schimburi si rapoarte</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => {
            setEditShift(null);
            setShiftForm({ name: '', start_time: '08:00', end_time: '17:00', color: '#3B82F6' });
            setShowShiftDialog(true);
          }} data-testid="add-shift-btn">
            <Plus className="w-4 h-4 mr-1" /> Schimb Nou
          </Button>
          <Button size="sm" onClick={handleAutoRotate} disabled={rotating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="auto-rotate-btn">
            {rotating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
            Genereaza Sapt. Urmatoare
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowReportDialog(true)} data-testid="report-settings-btn">
            <Settings className="w-4 h-4 mr-1" /> Raport Email
          </Button>
          <Button variant="outline" size="sm" onClick={handleClearWeek} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="clear-week-btn">
            <XCircle className="w-4 h-4 mr-1" /> Goleste Saptamana
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreviewReport} disabled={previewLoading} data-testid="preview-report-btn">
            {previewLoading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Previzualizeaza ({formatDateShort(weekDates[0])})
          </Button>
          <Button variant="outline" size="sm" onClick={handleSendReport} disabled={sendingReport} data-testid="send-report-btn">
            {sendingReport ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Trimite Raport ({formatDateShort(weekDates[0])})
          </Button>
        </div>
      </div>

      {/* On Duty Now */}
      {onDuty && onDuty.on_duty.length > 0 && (
        <Card className="border-green-200 bg-green-50" data-testid="on-duty-card">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-semibold text-green-800">Pe schimb acum ({onDuty.current_time}):</span>
              </div>
              {onDuty.on_duty.map((t, i) => (
                <Badge key={i} className="text-xs" style={{ backgroundColor: t.shift_color, color: '#fff' }}>
                  <UserCheck className="w-3 h-3 mr-1" />
                  {t.technician_name} ({t.shift_name})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shift Definitions */}
      <div className="flex flex-wrap gap-3" data-testid="shift-definitions">
        {shifts.map(s => (
          <Card key={s.id} className="w-auto">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-3 h-8 rounded-full" style={{ backgroundColor: s.color }} />
              <div>
                <p className="font-semibold text-sm">{s.name}</p>
                <p className="text-xs text-slate-500">{s.start_time} - {s.end_time}</p>
              </div>
              <div className="flex gap-1 ml-2">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                  setEditShift(s);
                  setShiftForm({ name: s.name, start_time: s.start_time, end_time: s.end_time, color: s.color });
                  setShowShiftDialog(true);
                }}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteShift(s.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {shifts.length === 0 && (
          <p className="text-sm text-slate-500 italic">Niciun schimb definit. Creati schimburi pentru a incepe planificarea.</p>
        )}
      </div>

      {/* Tabs: Calendar / History */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="calendar" data-testid="shifts-tab-calendar"><Calendar className="w-4 h-4 mr-1" /> Calendar</TabsTrigger>
          <TabsTrigger value="history" data-testid="shifts-tab-history"><History className="w-4 h-4 mr-1" /> Istoric</TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          {/* Week Navigation */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Planificare Saptamanala
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevWeek}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={goToday}>Azi</Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextWeek}><ChevronRight className="w-4 h-4" /></Button>
                  <span className="text-sm font-medium text-slate-600 ml-2">{weekLabel}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2">
              {loading ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              ) : shifts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Definiti mai intai schimburile pentru a vizualiza calendarul</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm" data-testid="shifts-calendar-table">
                    <thead>
                      <tr>
                        <th className="p-2 text-left text-xs font-semibold text-slate-500 w-24 border-b">Schimb</th>
                        {DAYS.map((day, i) => (
                          <th key={i} className={`p-2 text-center text-xs font-semibold border-b min-w-[130px] ${isToday(weekDates[i]) ? 'bg-blue-50 text-blue-700' : 'text-slate-500'}`}>
                            <div>{day}</div>
                            <div className="font-normal text-[10px]">{formatDateShort(weekDates[i])}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map(shift => (
                        <tr key={shift.id}>
                          <td className="p-2 border-b align-top">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: shift.color }} />
                              <div>
                                <p className="font-medium text-xs">{shift.name}</p>
                                <p className="text-[10px] text-slate-400">{shift.start_time}-{shift.end_time}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => openAssignDialog('', shift.id, 'week')}
                              className="mt-1.5 w-full text-[10px] py-1 px-2 rounded border border-dashed border-blue-300 text-blue-500 hover:bg-blue-50 hover:border-blue-400 transition-colors flex items-center justify-center gap-1"
                              data-testid={`assign-week-${shift.id}`}
                            >
                              <Plus className="w-3 h-3" /> Saptamana
                            </button>
                          </td>
                          {weekDates.map((date, dayIdx) => {
                            const cellAssignments = getAssignmentsForCell(date, shift.id);
                            return (
                              <td key={dayIdx} className={`p-1 border-b border-l align-top ${isToday(date) ? 'bg-blue-50/50' : ''}`}>
                                <div className="min-h-[50px] space-y-1">
                                  {cellAssignments.map(a => (
                                    <div key={a.id} className="group flex items-center justify-between p-1.5 rounded text-xs" style={{ backgroundColor: shift.color + '20' }}>
                                      <span className="font-medium truncate" style={{ color: shift.color }}>{a.technician_name}</span>
                                      <button onClick={() => handleDeleteAssignment(a.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => openAssignDialog(date, shift.id)}
                                    className="w-full p-1 rounded border border-dashed border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-colors text-xs flex items-center justify-center gap-1"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Today's Shifts */}
          {onDuty && onDuty.all_today.length > 0 && (
            <Card className="mt-4" data-testid="today-schedule-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Programul de Azi ({formatDateRo(onDuty.date)})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {onDuty.all_today.map((t, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${t.is_on_duty ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: t.shift_color }}>
                        {t.technician_name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{t.technician_name}</p>
                        <p className="text-xs text-slate-500">{t.shift_name}: {t.start_time} - {t.end_time}</p>
                      </div>
                      {t.is_on_duty && (
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" /> Istoric Schimburi
                </CardTitle>
                <Select value={historyMonths} onValueChange={setHistoryMonths}>
                  <SelectTrigger className="w-40" data-testid="history-months-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Ultima luna</SelectItem>
                    <SelectItem value="3">Ultimele 3 luni</SelectItem>
                    <SelectItem value="6">Ultimele 6 luni</SelectItem>
                    <SelectItem value="12">Ultimul an</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Niciun istoric disponibil pentru perioada selectata</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((week, wi) => (
                    <div key={week.week_start} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedWeek(expandedWeek === week.week_start ? null : week.week_start)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`history-week-${wi}`}
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {formatDateRo(week.week_start)} — {formatDateRo(week.week_end)}
                            </p>
                            <p className="text-xs text-slate-400">
                              {week.technicians?.length || 0} tehnicieni | {week.shifts_used?.join(', ')} | {week.total_assignments} asignari
                            </p>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedWeek === week.week_start ? 'rotate-90' : ''}`} />
                      </button>
                      {expandedWeek === week.week_start && (
                        <div className="border-t bg-slate-50 p-4">
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-xs">
                              <thead>
                                <tr>
                                  <th className="p-2 text-left border-b text-slate-500">Tehnician</th>
                                  <th className="p-2 text-left border-b text-slate-500">Schimb</th>
                                  <th className="p-2 text-left border-b text-slate-500">Data</th>
                                  <th className="p-2 text-left border-b text-slate-500">Interval</th>
                                </tr>
                              </thead>
                              <tbody>
                                {week.assignments
                                  .sort((a, b) => a.date.localeCompare(b.date))
                                  .map((a, ai) => (
                                  <tr key={a.id || ai} className="border-b border-slate-100">
                                    <td className="p-2 font-medium">{a.technician_name}</td>
                                    <td className="p-2">
                                      <Badge className="text-[10px]" style={{ backgroundColor: a.shift_color + '20', color: a.shift_color }}>
                                        {a.shift_name}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-slate-600">{formatDateRo(a.date)}</td>
                                    <td className="p-2 text-slate-400">{a.shift_start} - {a.shift_end}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Shift Definition Dialog */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editShift ? 'Editeaza Schimb' : 'Schimb Nou'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Denumire</Label>
              <Input value={shiftForm.name} onChange={e => setShiftForm({ ...shiftForm, name: e.target.value })} placeholder="ex: Dimineata" data-testid="shift-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ora Inceput</Label>
                <Select value={shiftForm.start_time} onValueChange={v => setShiftForm({ ...shiftForm, start_time: v })}>
                  <SelectTrigger data-testid="shift-start-input"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ora Sfarsit</Label>
                <Select value={shiftForm.end_time} onValueChange={v => setShiftForm({ ...shiftForm, end_time: v })}>
                  <SelectTrigger data-testid="shift-end-input"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Culoare</Label>
              <div className="flex gap-2">
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'].map(c => (
                  <button key={c} onClick={() => setShiftForm({ ...shiftForm, color: c })} className={`w-8 h-8 rounded-full border-2 transition-transform ${shiftForm.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveShift} disabled={!shiftForm.name} data-testid="save-shift-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asigneaza Tehnician</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Mode Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg" data-testid="assign-mode-toggle">
              <button
                onClick={() => setAssignForm(p => ({ ...p, assign_mode: 'day' }))}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  assignForm.assign_mode === 'day' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="assign-mode-day"
              >
                <Calendar className="w-4 h-4 inline mr-1" /> Zi Individuala
              </button>
              <button
                onClick={() => setAssignForm(p => ({ ...p, assign_mode: 'week' }))}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  assignForm.assign_mode === 'week' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="assign-mode-week"
              >
                <Users className="w-4 h-4 inline mr-1" /> Toata Saptamana
              </button>
            </div>
            
            {assignForm.assign_mode === 'day' ? (
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={assignForm.date} onChange={e => setAssignForm({ ...assignForm, date: e.target.value })} />
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">Saptamana: {weekLabel}</p>
                <p className="text-xs text-blue-600 mt-1">Tehnicianul va fi asignat automat pe {AUTO_ASSIGN_DAYS} zile (Luni-Vineri). Sambata se adauga manual.</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Schimb</Label>
              <Select value={assignForm.shift_id} onValueChange={v => setAssignForm({ ...assignForm, shift_id: v })}>
                <SelectTrigger data-testid="assign-shift-select"><SelectValue placeholder="Selecteaza schimb" /></SelectTrigger>
                <SelectContent>
                  {shifts.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name} ({s.start_time}-{s.end_time})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tehnician / Operator</Label>
              <Select value={assignForm.technician_id} onValueChange={v => setAssignForm({ ...assignForm, technician_id: v })}>
                <SelectTrigger data-testid="assign-tech-select"><SelectValue placeholder="Selecteaza persoana" /></SelectTrigger>
                <SelectContent>
                  {technicians.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.full_name}{t.function ? ` (${t.function})` : ''}
                    </SelectItem>
                  ))}
                  {technicians.length === 0 && (
                    <div className="p-3 text-center text-sm text-slate-500">
                      Niciun utilizator cu bifa "Lucreaza in schimburi".<br/>
                      Activati-o din Utilizatori → Editare → Pas 2.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Anuleaza</Button>
            <Button onClick={handleAssign} disabled={!assignForm.technician_id || !assignForm.shift_id || (assignForm.assign_mode === 'day' && !assignForm.date)} data-testid="save-assign-btn">
              <UserCheck className="w-4 h-4 mr-1" /> {assignForm.assign_mode === 'week' ? 'Asigneaza Saptamana' : 'Asigneaza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Settings Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Configurare Raport Saptamanal</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* SMTP Warning */}
            {!smtpConfigured && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="smtp-warning">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Serverul SMTP nu este configurat</p>
                  <p className="text-xs text-amber-600 mt-0.5">Accesati <strong>Setari &gt; Configurare Email</strong> pentru a configura serverul SMTP inainte de a trimite rapoarte prin email.</p>
                </div>
              </div>
            )}
            {/* Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Trimitere automata</p>
                <p className="text-xs text-slate-400">Raportul cu schimburile saptamanii urmatoare</p>
                {reportSettings.enabled && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                    Scheduler activ - se trimite automat {
                      (reportSettings.frequency || 'weekly') === 'daily' ? 'in fiecare zi' :
                      `in fiecare ${({monday:'Luni',tuesday:'Marti',wednesday:'Miercuri',thursday:'Joi',friday:'Vineri',saturday:'Sambata',sunday:'Duminica'}[reportSettings.send_day] || reportSettings.send_day)}`
                    } la {reportSettings.send_hour}
                  </p>
                )}
              </div>
              <Switch
                checked={reportSettings.enabled}
                onCheckedChange={v => setReportSettings(p => ({ ...p, enabled: v }))}
                data-testid="report-enabled-switch"
              />
            </div>

            {/* Channels */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Email</span>
                </div>
                <Switch
                  checked={reportSettings.send_email}
                  onCheckedChange={v => setReportSettings(p => ({ ...p, send_email: v }))}
                  data-testid="report-email-switch"
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  <span className="text-sm">SMS</span>
                </div>
                <Switch
                  checked={reportSettings.send_sms}
                  onCheckedChange={v => setReportSettings(p => ({ ...p, send_sms: v }))}
                  data-testid="report-sms-switch"
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Frecventa trimitere</Label>
                <Select value={reportSettings.frequency || 'weekly'} onValueChange={v => setReportSettings(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger data-testid="report-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">In fiecare zi</SelectItem>
                    <SelectItem value="weekly">Saptamanal (o zi anume)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Raportul trimis contine</Label>
                <Select value={reportSettings.report_week || 'next_week'} onValueChange={v => setReportSettings(p => ({ ...p, report_week: v }))}>
                  <SelectTrigger data-testid="report-week-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current_week">Saptamana curenta</SelectItem>
                    <SelectItem value="next_week">Saptamana urmatoare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {(reportSettings.frequency || 'weekly') === 'weekly' && (
                  <div>
                    <Label className="text-xs">Ziua trimiterii</Label>
                    <Select value={reportSettings.send_day} onValueChange={v => setReportSettings(p => ({ ...p, send_day: v }))}>
                      <SelectTrigger data-testid="report-day-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Luni</SelectItem>
                        <SelectItem value="tuesday">Marti</SelectItem>
                        <SelectItem value="wednesday">Miercuri</SelectItem>
                        <SelectItem value="thursday">Joi</SelectItem>
                        <SelectItem value="friday">Vineri</SelectItem>
                        <SelectItem value="saturday">Sambata</SelectItem>
                        <SelectItem value="sunday">Duminica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Ora trimiterii</Label>
                  <Select value={reportSettings.send_hour} onValueChange={v => setReportSettings(p => ({ ...p, send_hour: v }))}>
                    <SelectTrigger data-testid="report-hour-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, h) => {
                        const val = `${String(h).padStart(2, '0')}:00`;
                        return <SelectItem key={val} value={val}>{val}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-xs mb-2 block">Destinatari ({reportSettings.recipients.length})</Label>
              {reportSettings.recipients.length > 0 && (
                <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                  {reportSettings.recipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{r.name}</span>
                        {r.email && <span className="text-slate-400 ml-2">{r.email}</span>}
                        {r.phone && <span className="text-slate-400 ml-2">{r.phone}</span>}
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => removeRecipient(i)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="Nume"
                  value={newRecipient.name}
                  onChange={e => setNewRecipient(p => ({ ...p, name: e.target.value }))}
                  data-testid="recipient-name-input"
                />
                <Input
                  placeholder="Email"
                  value={newRecipient.email}
                  onChange={e => setNewRecipient(p => ({ ...p, email: e.target.value }))}
                  data-testid="recipient-email-input"
                />
                <div className="flex gap-1">
                  <Input
                    placeholder="Telefon"
                    value={newRecipient.phone}
                    onChange={e => setNewRecipient(p => ({ ...p, phone: e.target.value }))}
                    data-testid="recipient-phone-input"
                  />
                  <Button size="icon" variant="outline" onClick={addRecipient} data-testid="add-recipient-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Report Logs */}
          <div className="border-t pt-4 mt-2">
            <Label className="text-xs mb-2 block flex items-center gap-1">
              <History className="w-3 h-3" /> Istoric Rapoarte Trimise
            </Label>
            {reportLogsLoading ? (
              <div className="flex justify-center py-4"><RefreshCw className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : reportLogs.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3">Niciun raport trimis inca</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {reportLogs.map((log, i) => {
                  const emailOk = (log.email_results || []).some(r => r.success);
                  const smsOk = (log.sms_results || []).some(r => r.success);
                  const anyOk = emailOk || smsOk;
                  const isAuto = log._auto;
                  return (
                    <div key={log.id || i} className={`flex items-center gap-3 p-2 rounded text-xs border ${anyOk ? 'border-green-100 bg-green-50/50' : 'border-red-100 bg-red-50/50'}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${anyOk ? 'bg-green-500' : 'bg-red-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800">
                          {log.week || 'N/A'}
                          <span className="ml-2 text-slate-400 font-normal">
                            {isAuto ? '(automat)' : '(manual)'}
                          </span>
                        </p>
                        <p className="text-slate-400">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString('ro-RO') : '-'}
                          {' | '}{log.recipients_count || 0} destinatari
                          {emailOk && ' | Email OK'}
                          {smsOk && ' | SMS OK'}
                          {!anyOk && ' | Esuat'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveReportSettings} data-testid="save-report-settings-btn">Salveaza Setari</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Previzualizare Raport Schimburi</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div>
          ) : (
            <div className="border rounded-lg p-4 bg-white" dangerouslySetInnerHTML={{ __html: previewHtml }} data-testid="report-preview-content" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Inchide</Button>
            <Button onClick={handleSendReport} disabled={sendingReport} data-testid="send-from-preview-btn">
              {sendingReport ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Trimite Acum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftsPage;
